import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import MainPanelHeader from "../../layouts/mainPanelHeader";
import PanelRightContent from "../../layouts/mainPanel/PanelRightContent";
import { GlobalStateContext } from "../../layouts/GlobalStates";
import { ToastContext } from "../../component/global/ToastContext";
import { del, get, patch, post } from "../../controlers/global/ConfigRequest";
import {
  ADMIN_COMPANY_AVAILABLE_ADMINS,
  ADMIN_COMPANY_ADD_COINS,
  ADMIN_COMPANY_CREATE,
  ADMIN_COMPANY_DELETE,
  ADMIN_COMPANY_DETAIL,
  ADMIN_COMPANY_ENTER_AS_ADMIN,
  ADMIN_COMPANY_LIST,
  ADMIN_COMPANY_RESTORE,
  ADMIN_COMPANY_SUSPEND,
  ADMIN_PACKAGE_CREATE,
  ADMIN_PACKAGE_DETAIL,
  ADMIN_PACKAGE_LIST,
} from "../../helperData/ApiAddress";
import {
  applyPackageMenusToSidebar,
  createDefaultSidebarConfig,
  createEmptyMenuAccess,
  normalizeMenuConfig,
} from "../../helperData/organizationSidebar";
import {
  isImpersonating,
  stashSuperuserSession,
} from "../../helperData/orgImpersonation";
import MenuAccessFields from "./MenuAccessFields";
import AiUsageSettingsTab from "./AiUsageSettingsTab";
import ContentReviewTab from "./ContentReviewTab";
import UsersTab from "./UsersTab";
import "./style.scss";

const SUPERUSER_LEVEL = 1;

const emptyOrgForm = () => ({
  name: "",
  web_url: "",
  phone_number: "",
  email: "",
  contract_number: "",
  address: "",
  admin_user_id: "",
  package: "",
  coins_to_add: "",
  sidebar_config: createDefaultSidebarConfig(),
});

const emptyPackageForm = () => ({
  name: "",
  description: "",
  storage_volume: "",
  coins: "0",
  price_monthly: "0",
  price_six_month: "0",
  price_yearly: "0",
  is_default: false,
  is_base: false,
  trial_duration_days: "14",
  menu_access: createEmptyMenuAccess(),
});

const priceFieldFromPackage = (item, days) => {
  const field = {
    30: "price_monthly",
    180: "price_six_month",
    365: "price_yearly",
  }[days];
  if (item && item[field] != null && item[field] !== "") {
    return String(item[field]);
  }
  const match = (item?.prices || []).find(
    (price) => Number(price.duration_as_days) === days
  );
  return match != null ? String(match.price) : "0";
};

const buildOrgPayload = (form) => {
  const payload = {
    name: form.name.trim(),
    web_url: form.web_url.trim(),
    phone_number: form.phone_number.trim(),
    email: form.email.trim(),
    contract_number: form.contract_number.trim(),
    address: form.address.trim(),
    package: form.package ? Number(form.package) : null,
    sidebar_config: form.sidebar_config,
  };
  payload.admin_user_id = form.admin_user_id
    ? Number(form.admin_user_id)
    : null;
  const coinsToAdd = Number(form.coins_to_add);
  if (Number.isFinite(coinsToAdd) && coinsToAdd > 0) {
    payload.coins_to_add = coinsToAdd;
  }
  return payload;
};

const buildPackagePayload = (form) => ({
  name: form.name.trim(),
  description: form.description.trim(),
  storage_volume: Number(form.storage_volume),
  coins: Number(form.coins) || 0,
  menu_access: form.menu_access,
  active: true,
  show: true,
  is_default: Boolean(form.is_default),
  is_base: Boolean(form.is_base),
  trial_duration_days: Number(form.trial_duration_days) || 14,
  price_monthly: Number(form.price_monthly) || 0,
  price_six_month: Number(form.price_six_month) || 0,
  price_yearly: Number(form.price_yearly) || 0,
});

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return null;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail)) return data.detail.join(" ");
  const fieldErrors = Object.values(data).flat().filter(Boolean);
  return fieldErrors.length ? fieldErrors.join(" ") : null;
};

const AdminModalHeader = ({ title, onClose }) => (
  <Modal.Header>
    <Modal.Title>{title}</Modal.Title>
    <button
      type="button"
      className="btn-close"
      aria-label="Close"
      onClick={onClose}
    />
  </Modal.Header>
);

const Organizations = () => {
  const { auth, setAuth, setToggleSidebar } = useContext(GlobalStateContext);
  const show = useContext(ToastContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isSuperuser =
    Number(auth.usertype) === SUPERUSER_LEVEL || auth.is_superuser === true;

  const [activeTab, setActiveTab] = useState("organizations");
  const [loading, setLoading] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [packageQuery, setPackageQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyOrgForm());
  const [errors, setErrors] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageForm, setPackageForm] = useState(emptyPackageForm());
  const [packageErrors, setPackageErrors] = useState({});
  const [savingPackage, setSavingPackage] = useState(false);
  const [coinOrg, setCoinOrg] = useState(null);
  const [coinAmount, setCoinAmount] = useState("");
  const [coinError, setCoinError] = useState("");
  const [savingCoins, setSavingCoins] = useState(false);

  const loadAdminUsers = (companyId) => {
    get(
      ADMIN_COMPANY_AVAILABLE_ADMINS,
      companyId ? { company_id: companyId } : {}
    )
      .then((data) =>
        setAdminUsers(Array.isArray(data) ? data : data?.results || [])
      )
      .catch(() => show(t("OrgManagement.Error"), "danger"));
  };

  const fetchPackages = () => {
    setPackagesLoading(true);
    get(ADMIN_PACKAGE_LIST)
      .then((data) =>
        setPackages(Array.isArray(data) ? data : data?.results || [])
      )
      .catch(() => show(t("OrgManagement.Error"), "danger"))
      .finally(() => setPackagesLoading(false));
  };

  const openCreate = () => {
    setEditingOrg(null);
    setForm(emptyOrgForm());
    setErrors({});
    loadAdminUsers();
    if (!packages.length) fetchPackages();
    setShowCreate(true);
  };

  const openEdit = (org) => {
    const defaults = emptyOrgForm();
    setEditingOrg(org);
    setForm({
      ...defaults,
      name: org.name || "",
      web_url: org.web_url || "",
      phone_number: org.phone_number || "",
      email: org.email || "",
      contract_number: org.contract_number || "",
      address: org.address || "",
      admin_user_id: org.admin_user_id ? String(org.admin_user_id) : "",
      package: org.package ? String(org.package) : "",
      coins_to_add: "",
      sidebar_config: {
        ...defaults.sidebar_config,
        ...normalizeMenuConfig(org.sidebar_config || {}, { withLabels: true }),
      },
    });
    setErrors({});
    loadAdminUsers(org.id);
    if (!packages.length) fetchPackages();
    setShowCreate(true);
  };

  const fetchOrgs = () => {
    setLoading(true);
    get(ADMIN_COMPANY_LIST)
      .then((data) => setOrgs(Array.isArray(data) ? data : data?.results || []))
      .catch(() => show(t("OrgManagement.Error"), "danger"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isSuperuser && !isImpersonating()) {
      fetchOrgs();
      fetchPackages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperuser]);

  const filteredOrgs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => (o.name || "").toLowerCase().includes(q));
  }, [orgs, query]);

  const filteredPackages = useMemo(() => {
    const q = packageQuery.trim().toLowerCase();
    if (!q) return packages;
    return packages.filter((item) => (item.name || "").toLowerCase().includes(q));
  }, [packages, packageQuery]);

  if (!isSuperuser || isImpersonating()) {
    return (
      <div className="main_panel_box organizations_page">
        <MainPanelHeader />
        <div className="main_panel_content">
          <PanelRightContent>
            <div className="organizations_denied">
              {t("OrgManagement.Error")}
            </div>
          </PanelRightContent>
        </div>
      </div>
    );
  }

  const closeSidebar = () => {
    document.body.classList.remove("no_overflow");
    setToggleSidebar(false);
  };

  const applySession = (response) => {
    stashSuperuserSession({ id: auth.id, username: auth.username });

    window.sessionStorage.auth = response.access_token;
    window.sessionStorage.id = response.user.id;
    window.sessionStorage.username = response.user.username;
    window.sessionStorage.usertype = response.user.user_level;
    window.sessionStorage.usercompany = response.user.company;
    window.sessionStorage.first_name = response.user.first_name || "";
    window.sessionStorage.last_name = response.user.last_name || "";
    window.sessionStorage.package = JSON.stringify(response.user.package || {});
    window.sessionStorage.sidebar_config = JSON.stringify(
      response.user.sidebar_config || {}
    );
    window.sessionStorage.company_admin = response.user.company_admin;
    window.sessionStorage.is_superuser = false;
    window.sessionStorage.email = response.user.email || "";

    setAuth({
      ...auth,
      id: response.user.id,
      access_token: response.access_token,
      username: response.user.username,
      usertype: response.user.user_level,
      usercompany: response.user.company,
      first_name: response.user.first_name,
      last_name: response.user.last_name,
      package: response.user.package || {},
      sidebar_config: response.user.sidebar_config || {},
      company_admin: response.user.company_admin,
      is_superuser: false,
      email: response.user.email,
    });
  };

  const enterAsAdmin = (org) => {
    if (org.is_deleted || org.is_suspended) {
      show(t("OrgManagement.Error"), "danger");
      return;
    }
    setBusyId(org.id);
    post(ADMIN_COMPANY_ENTER_AS_ADMIN.replace("{id}", org.id))
      .then((response) => {
        applySession(response);
        show(t("OrgManagement.Enter.Success"), "success");
        closeSidebar();
        navigate("/dashboard");
      })
      .catch(() => show(t("OrgManagement.Error"), "danger"))
      .finally(() => setBusyId(null));
  };

  const toggleSuspend = (org) => {
    const next = !org.is_suspended;
    if (next && !window.confirm(t("OrgManagement.Suspend.Confirm"))) return;
    setBusyId(org.id);
    post(ADMIN_COMPANY_SUSPEND.replace("{id}", org.id))
      .then((updated) => {
        setOrgs((prev) =>
          prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o))
        );
        show(t("OrgManagement.Suspend.Success"), "success");
      })
      .catch(() => show(t("OrgManagement.Error"), "danger"))
      .finally(() => setBusyId(null));
  };

  const restoreOrg = (org) => {
    setBusyId(org.id);
    post(ADMIN_COMPANY_RESTORE.replace("{id}", org.id))
      .then((updated) => {
        setOrgs((prev) =>
          prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o))
        );
        show(t("OrgManagement.Restore.Success"), "success");
      })
      .catch(() => show(t("OrgManagement.Error"), "danger"))
      .finally(() => setBusyId(null));
  };

  const deleteOrg = (org) => {
    if (!window.confirm(t("OrgManagement.Delete.Confirm"))) return;
    setBusyId(org.id);
    del(ADMIN_COMPANY_DELETE.replace("{id}", org.id))
      .then(() => {
        setOrgs((prev) =>
          prev.map((o) =>
            o.id === org.id
              ? { ...o, is_deleted: true, is_suspended: false }
              : o
          )
        );
        show(t("OrgManagement.Delete.Success"), "success");
      })
      .catch(() => show(t("OrgManagement.Error"), "danger"))
      .finally(() => setBusyId(null));
  };

  const changeOrgPackage = (packageId) => {
    const selected = packages.find((item) => String(item.id) === String(packageId));
    setForm((prev) => ({
      ...prev,
      package: packageId,
      sidebar_config:
        !editingOrg && selected
          ? applyPackageMenusToSidebar(prev.sidebar_config, selected.menu_access)
          : prev.sidebar_config,
    }));
  };

  const submitOrganization = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = t("OrgManagement.Create.Name.Required");
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setCreating(true);
    const request = editingOrg
      ? patch(
          ADMIN_COMPANY_DETAIL.replace("{id}", editingOrg.id),
          buildOrgPayload(form)
        )
      : post(ADMIN_COMPANY_CREATE, buildOrgPayload(form));

    request
      .then(() => {
        fetchOrgs();
        setShowCreate(false);
        setEditingOrg(null);
        setForm(emptyOrgForm());
        setErrors({});
        show(
          t(
            editingOrg
              ? "OrgManagement.Edit.Success"
              : "OrgManagement.Create.Success"
          ),
          "success"
        );
      })
      .catch((error) => show(getApiErrorMessage(error) || t("OrgManagement.Error"), "danger"))
      .finally(() => setCreating(false));
  };

  const openCreatePackage = () => {
    setEditingPackage(null);
    setPackageForm(emptyPackageForm());
    setPackageErrors({});
    setShowPackageModal(true);
  };

  const openEditPackage = (item) => {
    setEditingPackage(item);
    setPackageForm({
      name: item.name || "",
      description: item.description || "",
      storage_volume: item.storage_volume != null ? String(item.storage_volume) : "",
      coins: item.coins != null ? String(item.coins) : "0",
      price_monthly: priceFieldFromPackage(item, 30),
      price_six_month: priceFieldFromPackage(item, 180),
      price_yearly: priceFieldFromPackage(item, 365),
      is_default: Boolean(item.is_default),
      is_base: Boolean(item.is_base),
      trial_duration_days: String(item.trial_duration_days || 14),
      menu_access: {
        ...createEmptyMenuAccess(),
        ...normalizeMenuConfig(item.menu_access || {}, { withLabels: false }),
      },
    });
    setPackageErrors({});
    setShowPackageModal(true);
  };

  const submitPackage = () => {
    const nextErrors = {};
    if (!packageForm.name.trim()) {
      nextErrors.name = t("OrgManagement.Packages.Name.Required");
    }
    const storage = Number(packageForm.storage_volume);
    if (!Number.isFinite(storage) || storage < 1) {
      nextErrors.storage_volume = t("OrgManagement.Packages.Storage.Required");
    }
    if (packageForm.is_default && packageForm.is_base) {
      nextErrors.role = t("OrgManagement.Packages.Role.Conflict");
    }
    if (packageForm.is_default) {
      const trialDays = Number(packageForm.trial_duration_days);
      if (!Number.isFinite(trialDays) || trialDays < 1) {
        nextErrors.trial_duration_days = t(
          "OrgManagement.Packages.TrialDays.Required"
        );
      }
    }
    ["price_monthly", "price_six_month", "price_yearly"].forEach((field) => {
      const amount = Number(packageForm[field]);
      if (!Number.isFinite(amount) || amount < 0) {
        nextErrors[field] = t("OrgManagement.Packages.Price.Invalid");
      }
    });
    setPackageErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSavingPackage(true);
    const payload = buildPackagePayload(packageForm);
    const request = editingPackage
      ? patch(ADMIN_PACKAGE_DETAIL.replace("{id}", editingPackage.id), payload)
      : post(ADMIN_PACKAGE_CREATE, payload);

    request
      .then(() => {
        fetchPackages();
        setShowPackageModal(false);
        setEditingPackage(null);
        setPackageForm(emptyPackageForm());
        show(
          t(
            editingPackage
              ? "OrgManagement.Packages.Edit.Success"
              : "OrgManagement.Packages.Create.Success"
          ),
          "success"
        );
      })
      .catch((error) =>
        show(getApiErrorMessage(error) || t("OrgManagement.Error"), "danger")
      )
      .finally(() => setSavingPackage(false));
  };

  const deletePackage = (item) => {
    if (!window.confirm(t("OrgManagement.Packages.Delete.Confirm"))) return;
    setBusyId(`pkg-${item.id}`);
    del(ADMIN_PACKAGE_DETAIL.replace("{id}", item.id))
      .then(() => {
        setPackages((prev) => prev.filter((pkg) => pkg.id !== item.id));
        show(t("OrgManagement.Packages.Delete.Success"), "success");
      })
      .catch((error) =>
        show(getApiErrorMessage(error) || t("OrgManagement.Error"), "danger")
      )
      .finally(() => setBusyId(null));
  };

  const openCoinModal = (org) => {
    setCoinOrg(org);
    setCoinAmount("");
    setCoinError("");
  };

  const submitCoins = () => {
    const amount = Number(coinAmount);
    if (!Number.isFinite(amount) || amount < 1) {
      setCoinError(t("OrgManagement.Coins.Amount.Required"));
      return;
    }
    setSavingCoins(true);
    post(ADMIN_COMPANY_ADD_COINS.replace("{id}", coinOrg.id), { amount })
      .then((data) => {
        setOrgs((prev) =>
          prev.map((org) =>
            org.id === coinOrg.id
              ? { ...org, coin_balance: data.coin_balance }
              : org
          )
        );
        setCoinOrg(null);
        setCoinAmount("");
        show(t("OrgManagement.Coins.Success"), "success");
      })
      .catch((error) =>
        show(getApiErrorMessage(error) || t("OrgManagement.Error"), "danger")
      )
      .finally(() => setSavingCoins(false));
  };

  const statusBadge = (org) => {
    if (org.is_deleted) return <span className="org_status deleted">{t("OrgManagement.Status.Deleted")}</span>;
    if (org.is_suspended) return <span className="org_status suspended">{t("OrgManagement.Status.Suspended")}</span>;
    if (!org.has_admin) return <span className="org_status no_admin">{t("OrgManagement.Status.NoAdmin")}</span>;
    return <span className="org_status active">{t("OrgManagement.Status.Active")}</span>;
  };

  const enabledMenuCount = (menuAccess) =>
    Object.values(menuAccess || {}).filter((item) => item?.enabled).length;

  return (
    <div className="main_panel_box organizations_page">
      <MainPanelHeader />
      <div className="main_panel_content">
        <PanelRightContent>
          <div
            className="organizations_tabs"
            role="tablist"
            aria-label={t("OrgManagement.Section.Title")}
          >
            <button
              type="button"
              role="tab"
              id="org-tab-organizations"
              aria-controls="org-panel-organizations"
              className={activeTab === "organizations" ? "active" : ""}
              aria-selected={activeTab === "organizations"}
              onClick={() => setActiveTab("organizations")}
            >
              {t("OrgManagement.Tab.Organizations")}
            </button>
            <button
              type="button"
              role="tab"
              id="org-tab-users"
              aria-controls="org-panel-users"
              className={activeTab === "users" ? "active" : ""}
              aria-selected={activeTab === "users"}
              onClick={() => setActiveTab("users")}
            >
              {t("OrgManagement.Tab.Users")}
            </button>
            <button
              type="button"
              role="tab"
              id="org-tab-packages"
              aria-controls="org-panel-packages"
              className={activeTab === "packages" ? "active" : ""}
              aria-selected={activeTab === "packages"}
              onClick={() => setActiveTab("packages")}
            >
              {t("OrgManagement.Tab.Packages")}
            </button>
            <button
              type="button"
              role="tab"
              id="org-tab-settings"
              aria-controls="org-panel-settings"
              className={activeTab === "settings" ? "active" : ""}
              aria-selected={activeTab === "settings"}
              onClick={() => setActiveTab("settings")}
            >
              {t("OrgManagement.Tab.Settings")}
            </button>
            <button
              type="button"
              role="tab"
              id="org-tab-content-review"
              aria-controls="org-panel-content-review"
              className={activeTab === "content-review" ? "active" : ""}
              aria-selected={activeTab === "content-review"}
              onClick={() => setActiveTab("content-review")}
            >
              {t("OrgManagement.Tab.ContentReview")}
            </button>
          </div>

          {activeTab === "organizations" ? (
            <div
              id="org-panel-organizations"
              role="tabpanel"
              aria-labelledby="org-tab-organizations"
            >
              <div className="organizations_toolbar">
                <div className="organizations_title">{t("OrgManagement.Section.Title")}</div>
                <div className="organizations_toolbar_actions">
                  <input
                    className="organizations_search"
                    type="text"
                    placeholder={t("OrgManagement.Search.Placeholder")}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  <Button variant="primary" onClick={openCreate}>
                    + {t("OrgManagement.New.Button")}
                  </Button>
                </div>
              </div>

              <div className="organizations_table_wrap">
                {loading ? (
                  <div className="organizations_loading">
                    <Spinner animation="border" size="sm" /> {t("OrgManagement.Loading")}
                  </div>
                ) : filteredOrgs.length === 0 ? (
                  <div className="organizations_empty">{t("OrgManagement.Empty")}</div>
                ) : (
                  <Table responsive className="organizations_table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t("OrgManagement.Create.Name")}</th>
                        <th>{t("OrgManagement.Package.Column")}</th>
                        <th>{t("OrgManagement.Coins.Column")}</th>
                        <th>{t("OrgManagement.Create.Phone")}</th>
                        <th>{t("OrgManagement.Admin.Label")}</th>
                        <th>{t("OrgManagement.Status.Active")}</th>
                        <th>{t("OrgManagement.Enter.Button")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrgs.map((org, idx) => (
                        <tr key={org.id} className={org.is_deleted ? "is_deleted" : ""}>
                          <td>{idx + 1}</td>
                          <td className="org_name_cell">{org.name}</td>
                          <td>{org.package_name || t("OrgManagement.Package.None")}</td>
                          <td className="org_coins_cell">
                            <span className="org_coin_value">
                              {Number(org.coin_balance || 0).toLocaleString()}
                            </span>
                          </td>
                          <td>{org.phone_number || "-"}</td>
                          <td>{org.admin_username || "-"}</td>
                          <td>{statusBadge(org)}</td>
                          <td className="org_actions_cell">
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={busyId === org.id || org.is_deleted || org.is_suspended}
                              onClick={() => enterAsAdmin(org)}
                            >
                              {busyId === org.id ? <Spinner animation="border" size="sm" /> : t("OrgManagement.Enter.Button")}
                            </Button>{" "}
                            <Button
                              size="sm"
                              variant="info"
                              disabled={busyId === org.id || org.is_deleted}
                              onClick={() => openEdit(org)}
                            >
                              {t("OrgManagement.Edit.Button")}
                            </Button>{" "}
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={busyId === org.id || org.is_deleted}
                              onClick={() => openCoinModal(org)}
                            >
                              {t("OrgManagement.Coins.Add")}
                            </Button>{" "}
                            {org.is_deleted ? (
                              <Button
                                size="sm"
                                variant="success"
                                disabled={busyId === org.id}
                                onClick={() => restoreOrg(org)}
                              >
                                {t("OrgManagement.Restore.Button")}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="warning"
                                disabled={busyId === org.id}
                                onClick={() => toggleSuspend(org)}
                              >
                                {org.is_suspended ? t("OrgManagement.Unsuspend.Button") : t("OrgManagement.Suspend.Button")}
                              </Button>
                            )}{" "}
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={busyId === org.id || org.is_deleted}
                              onClick={() => deleteOrg(org)}
                            >
                              {t("OrgManagement.Delete.Button")}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          ) : null}

          {activeTab === "packages" ? (
            <div
              id="org-panel-packages"
              role="tabpanel"
              aria-labelledby="org-tab-packages"
            >
              <div className="organizations_toolbar">
                <div className="organizations_title">{t("OrgManagement.Packages.Title")}</div>
                <div className="organizations_toolbar_actions">
                  <input
                    className="organizations_search"
                    type="text"
                    placeholder={t("OrgManagement.Packages.Search")}
                    value={packageQuery}
                    onChange={(e) => setPackageQuery(e.target.value)}
                  />
                  <Button variant="primary" onClick={openCreatePackage}>
                    + {t("OrgManagement.Packages.New")}
                  </Button>
                </div>
              </div>

              <div className="organizations_table_wrap">
                {packagesLoading ? (
                  <div className="organizations_loading">
                    <Spinner animation="border" size="sm" /> {t("OrgManagement.Loading")}
                  </div>
                ) : filteredPackages.length === 0 ? (
                  <div className="organizations_empty">{t("OrgManagement.Packages.Empty")}</div>
                ) : (
                  <Table responsive className="organizations_table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t("OrgManagement.Packages.Name")}</th>
                        <th>{t("OrgManagement.Packages.Role")}</th>
                        <th>{t("OrgManagement.Packages.Price.Monthly")}</th>
                        <th>{t("OrgManagement.Packages.Storage")}</th>
                        <th>{t("OrgManagement.Packages.GiftCoins")}</th>
                        <th>{t("OrgManagement.Packages.Menus")}</th>
                        <th>{t("OrgManagement.Edit.Button")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPackages.map((item, idx) => (
                        <tr key={item.id}>
                          <td>{idx + 1}</td>
                          <td className="org_name_cell">{item.name}</td>
                          <td>
                            {item.is_default ? (
                              <span className="org_pkg_badge default">
                                {t("OrgManagement.Packages.IsDefault.Badge")}
                              </span>
                            ) : null}
                            {item.is_base ? (
                              <span className="org_pkg_badge base">
                                {t("OrgManagement.Packages.IsBase.Badge")}
                              </span>
                            ) : null}
                            {!item.is_default && !item.is_base ? (
                              <span className="org_pkg_badge paid">
                                {t("OrgManagement.Packages.Paid.Badge")}
                              </span>
                            ) : null}
                          </td>
                          <td className="org_coins_cell">
                            {Number(
                              item.price_monthly ??
                                priceFieldFromPackage(item, 30)
                            ).toLocaleString()}
                          </td>
                          <td>{Number(item.storage_volume || 0).toLocaleString()}</td>
                          <td>{Number(item.coins || 0).toLocaleString()}</td>
                          <td>{enabledMenuCount(item.menu_access)}</td>
                          <td className="org_actions_cell">
                            <Button
                              size="sm"
                              variant="info"
                              disabled={busyId === `pkg-${item.id}`}
                              onClick={() => openEditPackage(item)}
                            >
                              {t("OrgManagement.Edit.Button")}
                            </Button>{" "}
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={busyId === `pkg-${item.id}`}
                              onClick={() => deletePackage(item)}
                            >
                              {t("OrgManagement.Delete.Button")}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          ) : null}

          {activeTab === "users" ? (
            <div
              id="org-panel-users"
              role="tabpanel"
              aria-labelledby="org-tab-users"
            >
              <UsersTab />
            </div>
          ) : null}

          {activeTab === "settings" ? <AiUsageSettingsTab /> : null}
          {activeTab === "content-review" ? <ContentReviewTab /> : null}
        </PanelRightContent>
      </div>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered size="lg" className="org_create_modal org_admin_modal">
        <AdminModalHeader
          title={t(
            editingOrg
              ? "OrgManagement.Edit.Title"
              : "OrgManagement.Create.Title"
          )}
          onClose={() => setShowCreate(false)}
        />
        <Modal.Body>
          <Form>
            <div className="org-form-grid">
              <Form.Group>
                <Form.Label htmlFor="org-name">
                  {t("OrgManagement.Create.Name")}
                </Form.Label>
                <Form.Control
                  id="org-name"
                  name="name"
                  type="text"
                  autoComplete="organization"
                  value={form.name}
                  isInvalid={!!errors.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="org-package">
                  {t("OrgManagement.Package.Label")}
                </Form.Label>
                <Form.Select
                  id="org-package"
                  name="package"
                  value={form.package}
                  onChange={(e) => changeOrgPackage(e.target.value)}
                >
                  <option value="">{t("OrgManagement.Package.None")}</option>
                  {packages.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text muted>{t("OrgManagement.Package.Help")}</Form.Text>
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="org-web-url">
                  {t("OrgManagement.Create.WebUrl")}
                </Form.Label>
                <Form.Control
                  id="org-web-url"
                  name="web_url"
                  type="url"
                  inputMode="url"
                  autoComplete="url"
                  value={form.web_url}
                  onChange={(e) => setForm({ ...form, web_url: e.target.value })}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="org-phone">
                  {t("OrgManagement.Create.Phone")}
                </Form.Label>
                <Form.Control
                  id="org-phone"
                  name="phone_number"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={form.phone_number}
                  onChange={(e) =>
                    setForm({ ...form, phone_number: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="org-email">
                  {t("OrgManagement.Create.Email")}
                </Form.Label>
                <Form.Control
                  id="org-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  spellCheck={false}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="org-contract">
                  {t("OrgManagement.Create.ContractNumber")}
                </Form.Label>
                <Form.Control
                  id="org-contract"
                  name="contract_number"
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  value={form.contract_number}
                  onChange={(e) =>
                    setForm({ ...form, contract_number: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="org-admin">
                  {t("OrgManagement.Admin.Label")}
                </Form.Label>
                <Form.Select
                  id="org-admin"
                  name="admin_user_id"
                  value={form.admin_user_id}
                  onChange={(e) =>
                    setForm({ ...form, admin_user_id: e.target.value })
                  }
                >
                  <option value="">{t("OrgManagement.Admin.None")}</option>
                  {adminUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.display_name} ({user.username})
                    </option>
                  ))}
                </Form.Select>
                <Form.Text muted>{t("OrgManagement.Admin.Help")}</Form.Text>
              </Form.Group>
              {editingOrg ? (
                <Form.Group>
                  <Form.Label htmlFor="org-coins-add">
                    {t("OrgManagement.Coins.Add")}
                  </Form.Label>
                  <Form.Control
                    id="org-coins-add"
                    name="coins_to_add"
                    type="number"
                    min="0"
                    inputMode="numeric"
                    value={form.coins_to_add}
                    onChange={(e) =>
                      setForm({ ...form, coins_to_add: e.target.value })
                    }
                  />
                  <Form.Text muted>
                    {t("OrgManagement.Coins.Current")}:{" "}
                    {Number(editingOrg.coin_balance || 0).toLocaleString()}
                  </Form.Text>
                </Form.Group>
              ) : null}
              <Form.Group className="org-form-span-2">
                <Form.Label htmlFor="org-address">
                  {t("OrgManagement.Create.Address")}
                </Form.Label>
                <Form.Control
                  id="org-address"
                  name="address"
                  as="textarea"
                  rows={2}
                  autoComplete="street-address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </Form.Group>
            </div>

            <MenuAccessFields
              t={t}
              value={form.sidebar_config}
              onChange={(sidebar_config) => setForm({ ...form, sidebar_config })}
              showLabels
              title={t("OrgManagement.Menu.Title")}
              hint={t("OrgManagement.Menu.Priority")}
              idPrefix="org"
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)} disabled={creating}>
            {t("OrgManagement.Create.Cancel")}
          </Button>
          <Button variant="primary" onClick={submitOrganization} disabled={creating}>
            {creating ? (
              <Spinner animation="border" size="sm" />
            ) : (
              t(
                editingOrg
                  ? "OrgManagement.Edit.Submit"
                  : "OrgManagement.Create.Submit"
              )
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showPackageModal}
        onHide={() => setShowPackageModal(false)}
        centered
        size="lg"
        className="org_create_modal org_admin_modal"
      >
        <AdminModalHeader
          title={t(
            editingPackage
              ? "OrgManagement.Packages.Edit.Title"
              : "OrgManagement.Packages.Create.Title"
          )}
          onClose={() => setShowPackageModal(false)}
        />
        <Modal.Body>
          <Form>
            <div className="org-form-grid">
              <Form.Group>
                <Form.Label htmlFor="package-name">
                  {t("OrgManagement.Packages.Name")}
                </Form.Label>
                <Form.Control
                  id="package-name"
                  name="package_name"
                  type="text"
                  value={packageForm.name}
                  isInvalid={!!packageErrors.name}
                  onChange={(e) =>
                    setPackageForm({ ...packageForm, name: e.target.value })
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {packageErrors.name}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="package-coins">
                  {t("OrgManagement.Packages.GiftCoins")}
                </Form.Label>
                <Form.Control
                  id="package-coins"
                  name="gift_coins"
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={packageForm.coins}
                  onChange={(e) =>
                    setPackageForm({ ...packageForm, coins: e.target.value })
                  }
                />
                <Form.Text muted>
                  {t("OrgManagement.Packages.GiftCoins.Help")}
                </Form.Text>
              </Form.Group>
              <Form.Group className="org-form-span-2">
                <Form.Label htmlFor="package-description">
                  {t("OrgManagement.Packages.Description")}
                </Form.Label>
                <Form.Control
                  id="package-description"
                  name="package_description"
                  as="textarea"
                  rows={3}
                  value={packageForm.description}
                  onChange={(e) =>
                    setPackageForm({
                      ...packageForm,
                      description: e.target.value,
                    })
                  }
                />
                <Form.Text muted>
                  {t("OrgManagement.Packages.Description.Help")}
                </Form.Text>
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="package-storage">
                  {t("OrgManagement.Packages.Storage")}
                </Form.Label>
                <Form.Control
                  id="package-storage"
                  name="storage_volume"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={packageForm.storage_volume}
                  isInvalid={!!packageErrors.storage_volume}
                  onChange={(e) =>
                    setPackageForm({
                      ...packageForm,
                      storage_volume: e.target.value,
                    })
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {packageErrors.storage_volume}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="package-price-monthly">
                  {t("OrgManagement.Packages.Price.Monthly")}
                </Form.Label>
                <Form.Control
                  id="package-price-monthly"
                  name="price_monthly"
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={packageForm.price_monthly}
                  isInvalid={!!packageErrors.price_monthly}
                  onChange={(e) =>
                    setPackageForm({
                      ...packageForm,
                      price_monthly: e.target.value,
                    })
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {packageErrors.price_monthly}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="package-price-six">
                  {t("OrgManagement.Packages.Price.SixMonth")}
                </Form.Label>
                <Form.Control
                  id="package-price-six"
                  name="price_six_month"
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={packageForm.price_six_month}
                  isInvalid={!!packageErrors.price_six_month}
                  onChange={(e) =>
                    setPackageForm({
                      ...packageForm,
                      price_six_month: e.target.value,
                    })
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {packageErrors.price_six_month}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="package-price-yearly">
                  {t("OrgManagement.Packages.Price.Yearly")}
                </Form.Label>
                <Form.Control
                  id="package-price-yearly"
                  name="price_yearly"
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={packageForm.price_yearly}
                  isInvalid={!!packageErrors.price_yearly}
                  onChange={(e) =>
                    setPackageForm({
                      ...packageForm,
                      price_yearly: e.target.value,
                    })
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {packageErrors.price_yearly}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="org-form-span-2 org_pkg_roles">
                <Form.Check
                  type="switch"
                  id="package-is-default"
                  checked={packageForm.is_default}
                  label={t("OrgManagement.Packages.IsDefault")}
                  onChange={(e) =>
                    setPackageForm({
                      ...packageForm,
                      is_default: e.target.checked,
                      is_base: e.target.checked ? false : packageForm.is_base,
                    })
                  }
                />
                <Form.Check
                  type="switch"
                  id="package-is-base"
                  checked={packageForm.is_base}
                  label={t("OrgManagement.Packages.IsBase")}
                  onChange={(e) =>
                    setPackageForm({
                      ...packageForm,
                      is_base: e.target.checked,
                      is_default: e.target.checked
                        ? false
                        : packageForm.is_default,
                    })
                  }
                />
                {packageErrors.role ? (
                  <div className="invalid-feedback d-block">
                    {packageErrors.role}
                  </div>
                ) : (
                  <Form.Text muted>
                    {t("OrgManagement.Packages.Role.Help")}
                  </Form.Text>
                )}
              </Form.Group>
              {packageForm.is_default ? (
                <Form.Group>
                  <Form.Label htmlFor="package-trial-days">
                    {t("OrgManagement.Packages.TrialDays")}
                  </Form.Label>
                  <Form.Control
                    id="package-trial-days"
                    name="trial_duration_days"
                    type="number"
                    min="1"
                    inputMode="numeric"
                    value={packageForm.trial_duration_days}
                    isInvalid={!!packageErrors.trial_duration_days}
                    onChange={(e) =>
                      setPackageForm({
                        ...packageForm,
                        trial_duration_days: e.target.value,
                      })
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {packageErrors.trial_duration_days}
                  </Form.Control.Feedback>
                  <Form.Text muted>
                    {t("OrgManagement.Packages.TrialDays.Help")}
                  </Form.Text>
                </Form.Group>
              ) : null}
            </div>
            <MenuAccessFields
              t={t}
              value={packageForm.menu_access}
              onChange={(menu_access) =>
                setPackageForm({ ...packageForm, menu_access })
              }
              title={t("OrgManagement.Packages.Menus")}
              idPrefix="package"
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPackageModal(false)}
            disabled={savingPackage}
          >
            {t("OrgManagement.Create.Cancel")}
          </Button>
          <Button variant="primary" onClick={submitPackage} disabled={savingPackage}>
            {savingPackage ? (
              <Spinner animation="border" size="sm" />
            ) : (
              t("OrgManagement.Packages.Submit")
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(coinOrg)}
        onHide={() => setCoinOrg(null)}
        centered
        className="org_admin_modal"
      >
        <AdminModalHeader
          title={t("OrgManagement.Coins.Title")}
          onClose={() => setCoinOrg(null)}
        />
        <Modal.Body>
          <Form>
            <p className="org_coin_modal_org">
              {coinOrg?.name} — {t("OrgManagement.Coins.Current")}:{" "}
              <strong>
                {Number(coinOrg?.coin_balance || 0).toLocaleString()}
              </strong>
            </p>
            <Form.Group>
              <Form.Label htmlFor="org-coin-amount">
                {t("OrgManagement.Coins.Amount")}
              </Form.Label>
              <Form.Control
                id="org-coin-amount"
                type="number"
                min="1"
                inputMode="numeric"
                value={coinAmount}
                isInvalid={!!coinError}
                onChange={(e) => {
                  setCoinAmount(e.target.value);
                  setCoinError("");
                }}
              />
              <Form.Control.Feedback type="invalid">
                {coinError}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCoinOrg(null)} disabled={savingCoins}>
            {t("OrgManagement.Create.Cancel")}
          </Button>
          <Button variant="primary" onClick={submitCoins} disabled={savingCoins}>
            {savingCoins ? (
              <Spinner animation="border" size="sm" />
            ) : (
              t("OrgManagement.Coins.Submit")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Organizations;
